/**
 * DriveLog.jsx — Day-grouped drive log with table fallback on desktop.
 *
 * Default view: mobile-first day cards (DriveDayCard). Desktop users can
 * toggle to the flat table for power editing.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Chip, Checkbox, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, MenuItem, Select, FormControl, InputLabel,
  Alert, Tooltip, Stack, ToggleButtonGroup, ToggleButton,
  CircularProgress, IconButton,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonalIcon,
  HelpOutline as UntaggedIcon,
  CheckCircle as ConfirmedIcon,
  PersonAdd as PersonAddIcon,
  ViewModule as CardsIcon,
  TableChart as TableIcon,
  AutoAwesome as SuggestIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import {
  useTessieDrives,
  useTessieDrivesByDay,
  useClassifyDrive,
  useBulkClassifyDrives,
  useAssignDriver,
  useBulkAssignDriver,
  useResuggest,
} from '../../hooks/useTessie';
import { useRoster } from '../../hooks/useRoster';
import { useMobile } from '../../hooks/useMobile';
import DriveDayCard from './DriveDayCard';
import DriveMapDialog from './DriveMapDialog';

const TAG_COLORS = { business: 'success', personal: 'error', untagged: 'default' };
const TAG_ICONS = {
  business: <BusinessIcon fontSize="small" />,
  personal: <PersonalIcon fontSize="small" />,
  untagged: <UntaggedIcon fontSize="small" />,
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function fmtMiles(m) {
  if (m == null) return '—';
  return `${parseFloat(m).toFixed(1)} mi`;
}
function shortenLocation(loc) {
  if (!loc) return '—';
  return loc.replace(/, United States$/, '').replace(/, California \d{5}/, ', CA');
}

export default function DriveLog({ myRosterId = null }) {
  const { data: roster = [] } = useRoster();
  const { isMobile } = useMobile();

  const [viewMode, setViewMode] = useState('cards');
  const effectiveView = isMobile ? 'cards' : viewMode;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterDriver, setFilterDriver] = useState('');
  const [filterConfirmed, setFilterConfirmed] = useState('');

  const activeDriverFilter = myRosterId || filterDriver;

  const filters = useMemo(() => ({
    from: dateFrom || undefined,
    to: dateTo ? `${dateTo}T23:59:59` : undefined,
    tag: filterTag || undefined,
    driver: activeDriverFilter || undefined,
    confirmed: filterConfirmed === '' ? undefined : filterConfirmed,
  }), [dateFrom, dateTo, filterTag, activeDriverFilter, filterConfirmed]);

  const classifyMut = useClassifyDrive();
  const bulkClassifyMut = useBulkClassifyDrives();
  const assignDriverMut = useAssignDriver();
  const bulkAssignMut = useBulkAssignDriver();
  const resuggestMut = useResuggest();

  const [mapDrive, setMapDrive] = useState(null);
  const handleOpenMap = useCallback((drive) => setMapDrive(drive), []);
  const handleCloseMap = useCallback(() => setMapDrive(null), []);

  const handleClassify = useCallback((id, tag) => {
    classifyMut.mutate({ id, tag });
  }, [classifyMut]);
  const handleAssignDriver = useCallback((id, rosterId) => {
    assignDriverMut.mutate({ id, rosterId });
  }, [assignDriverMut]);
  const handleAcceptSuggestion = useCallback((id, rosterId) => {
    assignDriverMut.mutate({ id, rosterId });
  }, [assignDriverMut]);
  const handleBulkClassifyIds = useCallback((ids, tag) => {
    if (!ids.length) return;
    bulkClassifyMut.mutate({ driveIds: ids, tag });
  }, [bulkClassifyMut]);
  const handleBulkAssignIds = useCallback((ids, rosterId) => {
    if (!ids.length || !rosterId) return;
    bulkAssignMut.mutate({ driveIds: ids, rosterId });
  }, [bulkAssignMut]);

  return (
    <Box>
      {/* Filter bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          type="date" label="From" size="small"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          sx={{ width: 150 }}
        />
        <TextField
          type="date" label="To" size="small"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          sx={{ width: 150 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Tag</InputLabel>
          <Select value={filterTag} label="Tag" onChange={(e) => setFilterTag(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="business">Business</MenuItem>
            <MenuItem value="personal">Personal</MenuItem>
            <MenuItem value="untagged">Untagged</MenuItem>
          </Select>
        </FormControl>
        {!myRosterId && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Driver</InputLabel>
            <Select value={filterDriver} label="Driver" onChange={(e) => setFilterDriver(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {roster.map((r) => (<MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterConfirmed} label="Status" onChange={(e) => setFilterConfirmed(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Reviewed</MenuItem>
            <MenuItem value="false">Unreviewed</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Recompute driver suggestions for unreviewed drives">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={resuggestMut.isPending ? <CircularProgress size={14} /> : <SuggestIcon />}
              onClick={() => resuggestMut.mutate()}
              disabled={resuggestMut.isPending}
              sx={{ minHeight: 32 }}
            >
              Resuggest
            </Button>
          </span>
        </Tooltip>

        {!isMobile && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={effectiveView}
            onChange={(_, v) => v && setViewMode(v)}
          >
            <ToggleButton value="cards" sx={{ px: 1.25 }}><CardsIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="table" sx={{ px: 1.25 }}><TableIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {effectiveView === 'cards' ? (
        <DayCardsView
          filters={filters}
          roster={roster}
          onClassify={handleClassify}
          onAssignDriver={handleAssignDriver}
          onAcceptSuggestion={handleAcceptSuggestion}
          onBulkClassify={handleBulkClassifyIds}
          onBulkAssign={handleBulkAssignIds}
          onOpenMap={handleOpenMap}
        />
      ) : (
        <TableView
          filters={filters}
          roster={roster}
          onClassify={handleClassify}
          onAssignDriver={handleAssignDriver}
          onAcceptSuggestion={handleAcceptSuggestion}
          onBulkClassify={handleBulkClassifyIds}
          onBulkAssign={handleBulkAssignIds}
          onOpenMap={handleOpenMap}
        />
      )}

      <DriveMapDialog open={!!mapDrive} onClose={handleCloseMap} drive={mapDrive} />
    </Box>
  );
}

function DayCardsView({ filters, roster, onClassify, onAssignDriver, onAcceptSuggestion, onBulkClassify, onBulkAssign, onOpenMap }) {
  const { data, isLoading } = useTessieDrivesByDay(filters);
  const days = data?.days || [];

  if (isLoading) {
    return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  }
  if (!days.length) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No drives match your filters.</Typography>
      </Paper>
    );
  }

  const unreviewedDays = days.filter((d) => d.unconfirmedCount > 0).length;

  return (
    <Box>
      {unreviewedDays > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <strong>{unreviewedDays}</strong> day{unreviewedDays === 1 ? '' : 's'} need review.
          Unreviewed drives become personal after the grace period.
        </Alert>
      )}
      {days.map((day, idx) => (
        <DriveDayCard
          key={day.date}
          day={day}
          roster={roster}
          defaultExpanded={idx === 0 && day.unconfirmedCount > 0}
          onClassify={onClassify}
          onAssignDriver={onAssignDriver}
          onAcceptSuggestion={onAcceptSuggestion}
          onBulkClassify={onBulkClassify}
          onBulkAssign={onBulkAssign}
          onOpenMap={onOpenMap}
        />
      ))}
    </Box>
  );
}

function TableView({ filters, roster, onClassify, onAssignDriver, onAcceptSuggestion, onBulkClassify, onBulkAssign, onOpenMap }) {
  const [page, setPage] = useState(0);
  const limit = 50;

  const tableFilters = useMemo(() => ({ ...filters, limit, offset: page * limit }), [filters, page]);
  const { data, isLoading } = useTessieDrives(tableFilters);
  const drives = data?.drives || [];
  const total = data?.total || 0;

  const [selected, setSelected] = useState(new Set());
  const [bulkDriver, setBulkDriver] = useState('');

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const selectAll = useCallback(() => {
    if (selected.size === drives.length) setSelected(new Set());
    else setSelected(new Set(drives.map((d) => d.id)));
  }, [drives, selected.size]);

  const runBulkClassify = (tag) => {
    onBulkClassify(Array.from(selected), tag);
    setSelected(new Set());
  };
  const runBulkAssign = () => {
    const ids = Array.from(selected);
    if (!ids.length || !bulkDriver) return;
    onBulkAssign(ids, bulkDriver);
    setSelected(new Set());
    setBulkDriver('');
  };

  return (
    <Box>
      {selected.size > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" sx={{ alignSelf: 'center', mr: 1 }}>
            {selected.size} selected:
          </Typography>
          <Button size="small" variant="contained" color="success" startIcon={<BusinessIcon />}
            onClick={() => runBulkClassify('business')}>Mark Business</Button>
          <Button size="small" variant="contained" color="error" startIcon={<PersonalIcon />}
            onClick={() => runBulkClassify('personal')}>Mark Personal</Button>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Assign Driver</InputLabel>
            <Select value={bulkDriver} label="Assign Driver" onChange={(e) => setBulkDriver(e.target.value)}>
              {roster.map((r) => (<MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>))}
            </Select>
          </FormControl>
          <Button size="small" variant="contained" startIcon={<PersonAddIcon />}
            onClick={runBulkAssign} disabled={!bulkDriver}>Assign</Button>
        </Stack>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 300px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={drives.length > 0 && selected.size === drives.length}
                  indeterminate={selected.size > 0 && selected.size < drives.length}
                  onChange={selectAll}
                />
              </TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell align="right">Miles</TableCell>
              <TableCell>From → To</TableCell>
              <TableCell align="center">Tag</TableCell>
              <TableCell align="center">Map</TableCell>
              <TableCell align="center">✓</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>Loading…</TableCell></TableRow>
            ) : drives.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No drives found</TableCell></TableRow>
            ) : (
              drives.map((d) => {
                const suggested = d.suggestedRosterId ? roster.find((r) => r.id === d.suggestedRosterId) : null;
                return (
                  <TableRow key={d.id} hover selected={selected.has(d.id)} sx={{ opacity: d.confirmed ? 1 : 0.9 }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">{fmtDate(d.startedAt)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtTime(d.startedAt)} – {fmtTime(d.endedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <FormControl size="small" variant="standard" sx={{ minWidth: 110 }}>
                          <Select
                            value={d.rosterId || ''}
                            onChange={(e) => onAssignDriver(d.id, e.target.value)}
                            displayEmpty
                            disableUnderline={!!d.rosterId}
                            sx={{
                              fontSize: '0.875rem',
                              '& .MuiSelect-select': { py: 0.25 },
                              color: d.rosterId ? 'text.primary' : 'text.secondary',
                            }}
                          >
                            <MenuItem value="" disabled><em>Assign driver</em></MenuItem>
                            {roster.map((r) => (<MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>))}
                          </Select>
                        </FormControl>
                        {suggested && !d.rosterId && (
                          <Tooltip title={`Accept suggestion (${d.suggestionReason})`}>
                            <Chip
                              size="small" variant="outlined" color="info"
                              label={`${suggested.name}?`}
                              onClick={() => onAcceptSuggestion(d.id, d.suggestedRosterId)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>{fmtMiles(d.distanceMiles)}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip title={`${d.startLocation || ''} → ${d.endLocation || ''}`}>
                        <Typography variant="caption" noWrap>
                          {shortenLocation(d.startLocation)} → {shortenLocation(d.endLocation)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={TAG_ICONS[d.tag]} label={d.tag}
                        color={TAG_COLORS[d.tag]} size="small"
                        onClick={() => onClassify(d.id, d.tag === 'business' ? 'personal' : 'business')}
                        sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {(d.startLat != null || d.endLat != null || d.startLocation || d.endLocation) ? (
                        <Tooltip title="View on map">
                          <IconButton size="small" onClick={() => onOpenMap(d)} aria-label="View on map">
                            <MapIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell align="center">
                      {d.confirmed ? (
                        <Tooltip title={`Confirmed by ${d.confirmedBy || '?'}`}>
                          <ConfirmedIcon fontSize="small" color="success" />
                        </Tooltip>
                      ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > limit && (
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
          <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>
            {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
          </Typography>
          <Button size="small" disabled={(page + 1) * limit >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </Stack>
      )}
    </Box>
  );
}
